
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PONGFOX_FIX_URL, PONGFOX_FIX_URL_DRILL_V1, PONGFOX_FIX_URL_DRILL_SEMIRANDOM, BACKEND_API_URL, BACKEND_API_URL_MYDRILLS_DEVICE } from "../constants/constants";
import DeviceIdCheck from './DeviceIdCheck';
import './CreateSessionNewUI.css';
import { Plus, Save, Trash2, Edit3 } from 'lucide-react';
import DrillSelectionPopup from './DrillSelectionPopup';

function CreateSession() {
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  const [selectedDrills, setSelectedDrills] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [drills, setDrills] = useState([]);
  const [dataV1, setDataV1] = useState([]);
  const [dataSemiRandom, setDataSemiRandom] = useState([]);
  const [dataCustomDrills, setDataCustomDrills] = useState([]);
  const [activeApi, setActiveApi] = useState('v1');
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState('All');
  const [preSelectedStrokes, setPreSelectedStrokes] = useState({});
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showDrillsPopup, setShowDrillsPopup] = useState(false);
  const [popupSessionDetails, setPopupSessionDetails] = useState([]);
  const [popupSessionTitle, setPopupSessionTitle] = useState('');

  const [showSelectedDrillsPopup, setShowSelectedDrillsPopup] = useState(false);
  const [showDrillSelectionPopup, setShowDrillSelectionPopup] = useState(false);
  const [showDeleteDrillPopup, setShowDeleteDrillPopup] = useState(false);
  const [drillToDelete, setDrillToDelete] = useState(null);
  const [showFooter, setShowFooter] = useState(true);
  const [originalSessionData, setOriginalSessionData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [onlyClearButtonVisible, setOnlyClearButtonVisible] = useState(false);

  const drillCounts = selectedDrills.reduce((acc, drill) => {
    acc[drill.id] = (acc[drill.id] || 0) + 1;
    return acc;
  }, {});



  useEffect(() => {
    let newHasChanges = false;
    if (isUpdateMode && originalSessionData) {
      const nameChanged = newSessionName !== originalSessionData.name;
      const descriptionChanged = newSessionDescription !== originalSessionData.description;

      const drillsChanged = (() => {
        if (selectedDrills.length !== originalSessionData.drills.length) {
          return true;
        }

        for (let i = 0; i < selectedDrills.length; i++) {
          const currentDrill = selectedDrills[i];
          const originalDrill = originalSessionData.drills[i];

          if (currentDrill.id !== originalDrill.id ||
            currentDrill.targetLocation !== originalDrill.targetLocation ||
            currentDrill.numberOfBalls !== originalDrill.numberOfBalls ||
            currentDrill.selectedStroke !== originalDrill.selectedStroke) {
            return true;
          }
        }

        return false;
      })();

      newHasChanges = nameChanged || descriptionChanged || drillsChanged;
      setHasChanges(newHasChanges);

    } else if (!isUpdateMode) {
      newHasChanges = newSessionName.trim() !== '' || selectedDrills.length > 0;
      setHasChanges(newHasChanges);
    }

    const saveButtonVisible = !isUpdateMode && newSessionName.trim() !== '' && selectedDrills.length > 0;
    const updateButtonVisible = isUpdateMode && newHasChanges;

    setOnlyClearButtonVisible(!saveButtonVisible && !updateButtonVisible);

  }, [newSessionName, newSessionDescription, selectedDrills, isUpdateMode, originalSessionData]);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchAllApis = async (currentDeviceId) => {
    try {
      const [resV1, resSemi, resCustom] = await Promise.all([
        fetch(PONGFOX_FIX_URL_DRILL_V1),
        fetch(PONGFOX_FIX_URL_DRILL_SEMIRANDOM),
        fetch(`${BACKEND_API_URL_MYDRILLS_DEVICE}/${currentDeviceId}`)
      ]);

      const [jsonV1, jsonSemi, jsonCustom] = await Promise.all([
        resV1.json(),
        resSemi.json(),
        resCustom.json()
      ]);

      const formatDrills = (drills, apiType) => drills.map(drill => ({
        ...drill,
        id: `${apiType}-${drill.id}`,
        apiType: apiType,
        desc: drill.desc || drill.description,
        strokes: drill.changeable_stroke_list || [],
        player_level: drill.player_level || "", // Ensure player_level is present and a string
      }));

      const formattedV1 = formatDrills(jsonV1, 'v1');
      const formattedSemi = formatDrills(jsonSemi, 'semirandom');
      const formattedCustom = jsonCustom.map(drill => {
        const parsedRoboJson = JSON.parse(drill.robo_json);
        return {
          ...drill,
          id: `custom_drills-${drill.id}`,
          drill_id: drill.id, // Add drill_id for custom drills
          apiType: "custom_drills",
          desc: drill.description,
          strokes: [], // Custom drills may not have this field, adjust as needed
          config: drill.robo_json,
          variations: Object.keys(parsedRoboJson.random).length - 1,
          feedrate: parsedRoboJson.frequency,
        };
      });

      const allDrills = [...formattedV1, ...formattedSemi, ...formattedCustom];

      // Initialize pre-selected strokes
      const initialStrokes = {};
      allDrills.forEach(drill => {
        if (drill.strokes && drill.strokes.length > 0) {
          initialStrokes[drill.id] = drill.strokes[0];
        }
      });
      setPreSelectedStrokes(initialStrokes);

      setDataV1(formattedV1);
      setDataSemiRandom(formattedSemi);
      setDataCustomDrills(formattedCustom);
      setDrills(allDrills);
    } catch (error) {
      console.error('Error fetching APIs:', error);
      toast.error('Failed to load drills.');
    }
  };

  const getSessions = async (currentDeviceId) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions/device/${currentDeviceId}`);
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      const sortedData = data.sort((a, b) => b.id - a.id);
      setSessions(sortedData);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load saved sessions.');
    }
  };

  // Helper function to validate and convert device ID (MAC or short ID like v183p)
  const convertDeviceIdToMac = async (id, shouldRedirectOnFailure = false) => {
    if (!id) return null;

    const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    const SHORT_ID_REGEX = /^[vV][0-9]+[pxPX]?$/; // valide it's a short ID format like v183p or v183 or v183x only

    // 1. Check if MAC valid ok
    if (MAC_REGEX.test(id)) {
      return id;
    } 
    // 2. Else check valid short id ok
    else if (SHORT_ID_REGEX.test(id)) {
      try {
        // 3. Then get mac
        const response = await fetch(`${PONGFOX_FIX_URL}/config/ble?id=${id}`);
        if (response.ok) {
          const data = await response.json();
          // 4. If get then return (set)
          if (data.android) {
            return data.android;
          } else {
            toast.error(`No MAC address found for device ID '${id}'.`);
          }
        } else {
          toast.error(`API failed to find device ID '${id}'.`);
        }
      } catch (error) {
        toast.error(`Error converting device ID '${id}'.`);
      }
    } 
    // 5. If not a valid small id then also redirect
    else {
      toast.error('Invalid device ID format provided.');
    }

    // 6. Handle redirect for failures during initial load
    if (shouldRedirectOnFailure) {
      navigate('/device?device_id=');
    }
    return null;
  };

  useEffect(() => {
    const deviceIdParam = new URLSearchParams(location.search).get('device_id');

    const initializeDevice = async () => {
      if (!deviceIdParam) {
        setDeviceId(null);
        return;
      }

      // 1. Get the MAC address for background work (APIs)
      const validatedMac = await convertDeviceIdToMac(deviceIdParam, true);
      
      if (validatedMac) {
        // 2. Keep the original ID (could be v183p) for the VIEW and STATE
        setDeviceId(deviceIdParam);
        
        // 3. Use the converted MAC address for the actual API calls
        fetchAllApis(validatedMac);
        getSessions(validatedMac);
      }
    };

    initializeDevice();
  }, [location.search, navigate]);

  const handlePreSelectStroke = (drillId, stroke) => {
    setPreSelectedStrokes(prev => ({ ...prev, [drillId]: stroke }));
  };

  const handleAddDrill = (drill) => {
    const count = selectedDrills.filter(item => item.title === drill.title).length;
    if (count >= 3) {
      toast.warn(`You can only add the same drill up to 3 times.`);
      return;
    }

    let checkDrill = 1, checkRandom = 0;
    let drillType = "";

    if (drill.apiType === "v1") {
      checkDrill = 1;
    }
    if (drill.isMixed === 1) {
      drillType = "isMixed";
    } else if (drill.apiType === "semirandom") {
      checkRandom = 1;
    }
    if (checkRandom) {
      drillType = "random";
    }
    if (drill.apiType === "custom_drills") {
      drillType = "isDirect";
    }
    if (drill.isMixedlevel) {
      drillType = "MixedLevel";
    }

    const newInstance = {
      ...drill,
      instanceId: `inst_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      targetLocation: '',
      numberOfBalls: 15,
      selectedStroke: preSelectedStrokes[drill.id] || (drill.strokes && drill.strokes.length > 0 ? drill.strokes[0] : null),
      isDrill: checkDrill,
      isRandom: checkRandom,
      drillType: drillType,
      repeat: 1,
      feedrate: drill.feedrate || 1.0,
      variations: drill.variations || 1,
      videoId: drill.videoId || "",
      random: drill.random || "", // Ensure it's a string, default to empty
      randomConfig: drill.randomConfig || "", // Ensure it's a string, default to empty
      tags: drill.tags || "",
      mirror: drill.mirror || "",
      pause: drill.pause || 0,
      player_level: drill.player_level || "", // Ensure player_level is always a string
    };
    setSelectedDrills(prev => [...prev, newInstance]);
  };

  const handleRemoveOneDrillInstance = (drillId) => {
    const indexToRemove = selectedDrills.findLastIndex(d => d.id === drillId);
    if (indexToRemove > -1) {
      setSelectedDrills(prev => prev.filter((_, index) => index !== indexToRemove));
    }
  };

  const handleRemoveDrillInstance = (instanceId) => {
    setSelectedDrills(prev => prev.filter(d => d.instanceId !== instanceId));
  };

  const handleUpdateSelectedDrill = (instanceId, field, value) => {
    setSelectedDrills(prev => prev.map(d => d.instanceId === instanceId ? { ...d, [field]: value } : d));
  };

  const handleStrokeSelection = (instanceId, stroke) => {
    setSelectedDrills(prev => prev.map(d => d.instanceId === instanceId ? { ...d, selectedStroke: stroke } : d));
  };

  const handleUpdatePopupDrill = (instanceId, field, value) => {
    setPopupSessionDetails(prev => prev.map(d => d.instanceId === instanceId ? { ...d, [field]: value } : d));
  };

  const handlePopupStrokeSelection = (instanceId, stroke) => {
    setPopupSessionDetails(prev => prev.map(d => d.instanceId === instanceId ? { ...d, selectedStroke: stroke } : d));
  };

  const handleUpdateFromPopup = () => {
    handleSaveOrUpdateSession(popupSessionDetails);
    setShowDrillsPopup(false);
  };

  const handleClearSelected = () => {
    setIsUpdateMode(false);
    setSessionId(null);
    setNewSessionName('');
    setNewSessionDescription('');
    setSelectedDrills([]);
    const isCreateNewSessionAction = !isUpdateMode && newSessionName.trim() === '' && selectedDrills.length === 0;
    setShowFooter(!isCreateNewSessionAction);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.getElementById('build').open = true;
    document.getElementById('saved').open = false;
  };

  const handleSaveOrUpdateSession = async (drillsToSave) => {
    let drills = drillsToSave || selectedDrills;
    if (!newSessionName.trim()) {
      toast.error('Please enter a Session Name before saving.');
      return;
    }

    if (drills.length === 0) {
      toast.error('At least one drill item must be selected');
      return;
    }

    // Validate and clamp the numberOfBalls for each drill
    const validatedDrills = drills.map(item => ({
      ...item,
      numberOfBalls: Math.min(Math.max(parseInt(item.numberOfBalls, 10) || 10, 10), 75)
    }));
    
    // Update the state to reflect the validated numbers in the UI
    setSelectedDrills(validatedDrills);
    
    // Use the validated drills for processing
    drills = validatedDrills;

    const processedSessionDetails = drills.map((item, index) => {
      let updatedConfig = item.config;
      if (Array.isArray(item.strokes) && item.strokes.length > 0) {
        const selectedStroke = item.selectedStroke || item.strokes[0];
        if (selectedStroke && updatedConfig) {
          let configObj = typeof updatedConfig === 'string' ? JSON.parse(updatedConfig) : { ...updatedConfig };
          Object.keys(configObj).forEach(key => {
            if (configObj[key] && typeof configObj[key] === 'object') {
              configObj[key].strokeType = selectedStroke;
            }
          });
          updatedConfig = JSON.stringify(configObj);
        }
      }

      // Ensure config is always a string
      if (typeof updatedConfig !== 'string') {
        updatedConfig = JSON.stringify(updatedConfig);
      }

      return {
        title: item.title,
        apitype: item.apiType || item.apitype,
        isDrill: item.isDrill,
        drill_id: item.drill_id || null,
        isRandom: item.isRandom,
        srno: parseInt(index + 1) || index + 1,
        numberOfBalls: item.numberOfBalls,
        taskDuration: item.numberOfBalls * 1.0,
        variations: item.variations,
        feedrate: item.feedrate,
        repeat: item.repeat,
        level: item.level || "",
        isMixedlevel: item.isMixedlevel || false,
        drillType: item.drillType,
        drillDescription: item.drillDescription || item.desc || '',
        tags: Array.isArray(item.tags) ? item.tags.join(',') : (item.tags || ''),
        mirror: item.mirror,
        player_level: item.player_level,
        videoId: item.videoId,
        config: updatedConfig || '{}',
        random: (typeof item.random === 'string' ? item.random : JSON.stringify(item.random)) || '{}',
        randomConfig: (typeof item.randomConfig === 'string' ? item.randomConfig : JSON.stringify(item.randomConfig)) || '{}',
        pause: item.pause,
        device_id: deviceId,
        target_location: item.targetLocation || null,
      };
    });

    try {
      // Convert device ID to MAC address if it's a short format (v183p -> C0:CD:D6:CE:5C:4E)
      const convertedDeviceId = await convertDeviceIdToMac(deviceId) || deviceId;

      const sessionData = {
        title: newSessionName.trim(),
        description: newSessionDescription.trim(),
        device_id: convertedDeviceId, // Use MAC address instead of short ID
        session_details: processedSessionDetails.map(detail => ({
          ...detail,
          device_id: convertedDeviceId // Update device_id in each session detail
        })),
        duration: drills.reduce((acc, item) => acc + (item.numberOfBalls * 1.0), 0),
        ...(isUpdateMode && { id: sessionId }), // Conditionally add id for update mode
      };

      let response;
      if (isUpdateMode) {
        const endpoint = `${BACKEND_API_URL}/session/update/${convertedDeviceId}/${sessionId}`;
        response = await axios.put(endpoint, { ...sessionData, id: sessionId });
        toast.success(`${sessionData.title} Updated Successfully!`);
        setNewSessionName('');
        setNewSessionDescription('');
        setSelectedDrills([]);
        setIsUpdateMode(false);
        setSessionId(null);
        document.getElementById('build').open = false;
        document.getElementById('saved').open = true;
      } else {
        const endpoint = `${BACKEND_API_URL}/custom/save`;
        response = await axios.post(endpoint, sessionData);
        toast.success(`${sessionData.title} Saved Successfully!`);
        // Clear the form to allow for new session creation
        setNewSessionName('');
        setNewSessionDescription('');
        setSelectedDrills([]);
        setIsUpdateMode(false);
        setSessionId(null);
        document.getElementById('build').open = false;
        document.getElementById('saved').open = true;
      }
      getSessions(convertedDeviceId); // Refresh saved sessions list using converted device ID
      fetchAllApis(convertedDeviceId); // Re-fetch drills to get any updates
    } catch (error) {
      console.error('Error saving session:', error.response || error.message || error);
      toast.error(`Failed to ${isUpdateMode ? 'update' : 'save'} session.`);
    }
  };

  const handleEnterEditMode = async (session) => {
    try {
      const address = `${BACKEND_API_URL}/session/id/${session.device_id}/${session.id}`;
      const response = await fetch(address);
      const data = await response.json();

      const sessionDetails = data.session_details || data.tasks || [];

      const mergedSelectedItems = sessionDetails.map(sessionDrill => {
        const drillFromList = drills.find(d => d.title === sessionDrill.title && d.apiType === sessionDrill.apitype);

        if (!drillFromList) {
          return null;
        }

        let strokeFromConfig = null;
        if (Array.isArray(drillFromList.strokes) && drillFromList.strokes.length > 0 && sessionDrill.config) {
          try {
            const configObj = typeof sessionDrill.config === 'string' ? JSON.parse(sessionDrill.config) : sessionDrill.config;
            const firstKey = Object.keys(configObj)[0];
            if (firstKey && configObj[firstKey] && configObj[firstKey].strokeType) {
              strokeFromConfig = configObj[firstKey].strokeType;
            }
          } catch {
            // JSON parsing error, ignore
          }
        }

        return {
          ...drillFromList,
          ...sessionDrill,
          id: drillFromList.id,
          instanceId: `inst_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          selectedStroke: strokeFromConfig || (drillFromList.strokes ? drillFromList.strokes[0] : null),
          targetLocation: sessionDrill.target_location || null,
        };
      }).filter(Boolean);

      setPopupSessionTitle(data.title);
      setPopupSessionDetails(mergedSelectedItems);
      setShowDrillsPopup(true);
      setShowFooter(false);

    } catch (error) {
      console.error("Error loading session details:", error);
      toast.error("Failed to load session details.");
    }
  };

  const handleEditSession = async (session) => {
    try {
      const address = `${BACKEND_API_URL}/session/id/${session.device_id}/${session.id}`;
      const response = await fetch(address);
      const data = await response.json();

      const sessionDetails = data.session_details || data.tasks || [];

      const mergedSelectedItems = sessionDetails.map(sessionDrill => {
        const drillFromList = drills.find(d => d.title === sessionDrill.title && d.apiType === sessionDrill.apitype);

        if (!drillFromList) {
          return null;
        }

        let strokeFromConfig = null;
        if (Array.isArray(drillFromList.strokes) && drillFromList.strokes.length > 0 && sessionDrill.config) {
          try {
            const configObj = typeof sessionDrill.config === 'string' ? JSON.parse(sessionDrill.config) : sessionDrill.config;
            const firstKey = Object.keys(configObj)[0];
            if (firstKey && configObj[firstKey] && configObj[firstKey].strokeType) {
              strokeFromConfig = configObj[firstKey].strokeType;
            }
          } catch {
            // JSON parsing error, ignore
          }
        }

        return {
          ...drillFromList,
          ...sessionDrill,
          id: drillFromList.id,
          instanceId: `inst_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          selectedStroke: strokeFromConfig || (drillFromList.strokes ? drillFromList.strokes[0] : null),
          targetLocation: sessionDrill.target_location || '',
        };
      }).filter(Boolean);

      setSessionId(data.id);
      setIsUpdateMode(true);
      setNewSessionName(data.title);
      setNewSessionDescription(data.description);
      setSelectedDrills(mergedSelectedItems);
      setOriginalSessionData({ name: data.title, description: data.description, drills: mergedSelectedItems });
      setHasChanges(false); // Reset changes when entering edit mode
      setShowFooter(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.getElementById('build').open = true;

    } catch (error) {
      console.error("Error loading session details:", error);
      toast.error("Failed to load session details.");
    }
  };

  const handleSessionDelete = (session) => {
    setSessionToDelete(session);
    setShowDeletePopup(true);
  };

  const handleCustomDrillDeleteRequest = (drillId) => {
    const drill = dataCustomDrills.find(d => d.drill_id === drillId);
    setDrillToDelete(drill);
    setShowDeleteDrillPopup(true);
  };

  const confirmDrillDelete = () => {
    if (drillToDelete) {
      handleCustomDrillDelete(drillToDelete.drill_id);
      setShowDeleteDrillPopup(false);
      setDrillToDelete(null);
    }
  };

  const cancelDrillDelete = () => {
    setShowDeleteDrillPopup(false);
    setDrillToDelete(null);
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;
    try {
      const address = `${BACKEND_API_URL}/session/${sessionToDelete.device_id}/${sessionToDelete.id}`;
      await axios.delete(address);
      toast.success('Session deleted successfully!');
      getSessions(sessionToDelete.device_id); // Use the device_id from the deleted session
      if (sessionId === sessionToDelete.id) {
        handleClearSelected();
      } else {
        setShowFooter(true);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session.');
      setShowFooter(true);
    } finally {
      setShowDeletePopup(false);
      setSessionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setSessionToDelete(null);
    setShowFooter(true);
  };

  const handleCustomDrillDelete = async (drillId) => {
    if (!deviceId || !drillId) {
      toast.error("Device ID or Drill ID is missing");
      return;
    }
    const address = `${BACKEND_API_URL}/drills/${deviceId}/${drillId}`;
    try {
      const response = await fetch(address, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Drill deleted successfully!');
        fetchAllApis(deviceId); // Re-fetch all drills
      } else {
        const errorJson = await response.json();
        const sessionName = errorJson.detail;
        const errorMessage = (
          <>
            This Drill is in use in Session : <b>{sessionName}</b>. You can't delete it.
          </>
        );
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting drill:', error);
      toast.error('Unexpected error occurred. Please try again.');
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedItems = Array.from(selectedDrills);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);
    setSelectedDrills(reorderedItems);
  };

  const getFilteredDrills = () => {
    const source = activeApi === 'v1' ? dataV1 : activeApi === 'semirandom' ? dataSemiRandom : dataCustomDrills;
    if (selectedVariation === 'All') {
      return source;
    }
    return source.filter(d => d.variations === selectedVariation);
  };

  if (!deviceId) {
    return <DeviceIdCheck />;
  }

  return (
    <div className="wrap">
      <ToastContainer />
      <>
        <div className="title">
          <h1>Device ID:</h1>
          <span className="chip"> {deviceId}</span>
        </div>
      </>

      <details className="section build" id="build">
        <summary><span className="dot"></span> {isUpdateMode ? 'Edit Session' : 'Create Session'}</summary>
        <div className="body">
          {/* <p className="sub">Name & describe your session, pick a variation, then add drills below.</p> */}

          <label htmlFor="sessionName" className="strong-label">Session Name</label>
          <input id="sessionName" type="text" placeholder="e.g., Warm-up Drill" value={newSessionName} onChange={(e) => setNewSessionName(e.target.value)} />

          <label htmlFor="sessionDesc" className="strong-label">Session Description</label>
          <input id="sessionDesc" type="text" placeholder="Goals, notes" value={newSessionDescription} onChange={(e) => setNewSessionDescription(e.target.value)} />

          <div className="section" style={{ marginTop: '12px', borderColor: '#ffe3cf', background: '#fff' }}>
            <div style={{ background: '#fff7ed', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '900', letterSpacing: '.4px', textTransform: 'uppercase', fontSize: '12px', borderBottom: '1px solid #fde68a', borderTopLeftRadius: '18px', borderTopRightRadius: '18px' }}>
              <span className="dot" style={{ background: 'var(--brand)' }}></span> Drill List 
              {/* {(isUpdateMode || selectedDrills.length > 0) && (
                <button
                  className="btn ghost"
                  style={{ marginLeft: '10px' }}
                  onClick={() => setShowSelectedDrillsPopup(true)}>
                  View Selected Drills
                </button>
              )} */}
              <div className="drill-buttons-container" style={{ display: 'flex', alignItems: 'center', gap: '1px', marginLeft: 'auto', justifyContent: 'flex-end' }}>
                <button
                  className="btn attention"
                  style={{ fontSize: '16px', padding: '8px 10px' }}
                  onClick={() => { setShowDrillSelectionPopup(true); setShowFooter(false); }}
                >
                  + Add Drills
                </button>
                {!isUpdateMode && (
                  <button id="newCustomBtn" className="btn attention" style={{ fontSize: '12px', padding: '8px 10px' }} onClick={() => navigate('/createDrill', { state: { device_id: `${deviceId}`, selectedItems: selectedDrills } })}>
                    + Create Custom Drill
                  </button>
                )}
              </div>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="body">
                {selectedDrills.length === 0 && (
                  <p className="sub">Drag to reorder. Each “+” adds another instance of the drill here. Set target & balls for each. Remove with “×”.</p>
                )}
                <Droppable droppableId="selected-drills">
                  {(provided) => (
                    <div id="selectedList" className="list" style={{ maxHeight: '400px', overflowY: 'auto' }} {...provided.droppableProps} ref={provided.innerRef}>
                      {selectedDrills.length === 0 ? (
                        <p id="emptySelected" className="sub">Nothing added yet.</p>
                      ) : (
                        selectedDrills.map((item, index) => {
                          const hasStrokes = Array.isArray(item.strokes) && item.strokes.length > 0;
                          return (
                            <Draggable key={item.instanceId} draggableId={item.instanceId} index={index}>
                              {(provided) => (
                                <div className="row" ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                  <button className="kill" title="Remove" onClick={() => handleRemoveDrillInstance(item.instanceId)}>×</button>
                                  <div className="meta">
                                    <div className="r-title" style={{
                                      textAlign: 'left',
                                      wordWrap: 'break-word',
                                      whiteSpace: 'pre-wrap',
                                      maxWidth: '70%'
                                    }}>{item.title}</div>
                                    <div className="pillset">
                                      {hasStrokes && item.strokes.map(s => (
                                        <div
                                          key={s}
                                          className="pill"
                                          role="button"
                                          tabIndex="0"
                                          aria-selected={item.selectedStroke === s}
                                          onClick={() => handleStrokeSelection(item.instanceId, s)}
                                          onKeyDown={(e) => e.key === 'Enter' && handleStrokeSelection(item.instanceId, s)} >
                                          {s}
                                        </div>
                                      ))}
                                    </div>
                                    <div className="controls responsive-controls" style={{ marginTop: hasStrokes ? '8px' : '20px' }}>

                                      <div className="control-item target-location-item">
                                        <input type="text" value={item.targetLocation} placeholder="e.g., Backhand corner" aria-label="Target Location" onChange={e => handleUpdateSelectedDrill(item.instanceId, 'targetLocation', e.target.value)} />
                                      </div>
                                      <div className="control-item">
                                        <div className="qty">

                                          <button type="button" onClick={() => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', Math.max(0, item.numberOfBalls - 1))}>-</button>
                                          <input type="text" inputMode="numeric" value={item.numberOfBalls} aria-label="balls" onChange={e => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', parseInt(e.target.value, 10) || 0)} />
                                          <button type="button" onClick={() => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', item.numberOfBalls + 1)}>+</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
              {/* </details> */}
            </DragDropContext>
          </div>
        </div>
      </details>

      <details className="section saved" id="saved" open>
        <summary><span className="dot"></span> Saved Sessions</summary>
        <div className="body">
          <div id="savedList" className="list" style={{ marginTop: '10px' }}>
            {sessions.length === 0 ? (
              <p id="emptySaved" className="sub">No saved sessions yet.</p>
            ) : (
              sessions.map((s, index) => {
                const isLastItem = index >= sessions.length - 2;
                return (
                  <div key={s.id} className="session-row">
                    <div onClick={() => handleEnterEditMode(s)} onKeyDown={e => e.key === 'Enter' && handleEnterEditMode(s)} style={{ flexGrow: 1, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: '0' }}>{s.title}</h3>
                        <div className="row-actions" style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn ghost" style={{ border: 'none', padding: '4px' }} onClick={(e) => { e.stopPropagation(); handleEditSession(s); }}>
                            <Edit3 size={18} />
                          </button>
                          <button className="btn ghost danger" style={{ border: 'none', padding: '4px' }} onClick={(e) => { e.stopPropagation(); handleSessionDelete(s); }}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <p style={{ margin: '4px 0 0', textAlign: 'left' }}>{s.description || ''}</p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </details>

      {showDeletePopup && sessionToDelete && (
        <>
          <div className="popup-backdrop" onClick={cancelDelete}></div>
          <div className="delete-popup">
            <h3>Delete Session</h3>
            <p>Are you sure you want to delete session "{sessionToDelete.title}"?</p>
            <div>
              <button className="btn danger" onClick={confirmDelete}>Delete</button>
              <button className="btn ghost" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {showDeleteDrillPopup && drillToDelete && (
        <>
          <div className="popup-backdrop" onClick={cancelDrillDelete}></div>
          <div className="delete-popup">
            <h3>Delete Drill</h3>
            <p>Are you sure you want to delete drill "{drillToDelete.title}"?</p>
            <div>
              <button className="btn danger" onClick={confirmDrillDelete}>Delete</button>
              <button className="btn ghost" onClick={cancelDrillDelete}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {showDrillsPopup && (
        <>
          <div className="popup-backdrop" onClick={() => { setShowDrillsPopup(false); setShowFooter(true); }}></div>
          <div className="drills-popup">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>{popupSessionTitle}</h3>
              <button className="btn ghost" onClick={() => { setShowDrillsPopup(false); setShowFooter(true); }}>Close</button>
            </div>
            <div className="list">
              {popupSessionDetails.length === 0 ? (
                <p className="sub">No drills in this session.</p>
              ) : (
                popupSessionDetails.map((item, index) => (
                  <div className="row" key={item.instanceId} style={{ textAlign: 'left' }}>
                    <div className="meta">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ width: '80%' }}>
                          <div className="r-title">{item.title}</div>
                        </div>
                        <div style={{ width: '40%', textAlign: 'right' }}>
                          <p style={{ margin: '0', fontSize: '14px', color: '#555' }}>Balls: {item.numberOfBalls}</p>
                        </div>
                      </div>
                      <div className="pillset" style={{ justifyContent: 'flex-start', marginTop: '8px' }}>
                        {Array.isArray(item.strokes) && item.strokes.map(s => (
                          <div
                            key={s}
                            className="pill"
                            aria-selected={item.selectedStroke === s}
                          >
                            {s}
                          </div>
                        ))}
                      </div>
                      <div className="controls" style={{ marginTop: '8px', textAlign: 'left' }}>
                        {item.targetLocation ? <p style={{ margin: 0, fontSize: '14px', color: '#555' }}><strong>Target:</strong> {item.targetLocation}</p> : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {showSelectedDrillsPopup && (
        <>
          <div className="popup-backdrop" onClick={() => setShowSelectedDrillsPopup(false)}></div>
          <div className="drills-popup">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h3>Selected Drills</h3>
              <button className="btn ghost" onClick={() => setShowSelectedDrillsPopup(false)}>Close</button>
            </div>
            <div className="list">
              {selectedDrills.length === 0 ? (
                <p className="sub">No drills selected.</p>
              ) : (
                selectedDrills.map((item, index) => (
                  <div className="row" key={item.instanceId}>
                    <div className="meta">
                      <div className="r-title">{item.title}</div>
                      <div className="pillset">
                        {Array.isArray(item.strokes) && item.strokes.map(s => (
                          <div
                            key={s}
                            className="pill"
                            role="button"
                            tabIndex="0"
                            aria-selected={item.selectedStroke === s}
                            onClick={() => handleStrokeSelection(item.instanceId, s)}
                            onKeyDown={(e) => e.key === 'Enter' && handleStrokeSelection(item.instanceId, s)} >
                            {s}
                          </div>
                        ))}
                      </div>
                      <div className="controls">
                        <input type="text" value={item.targetLocation} placeholder="Target Location (e.g., wide FH)" aria-label="Target Location" onChange={e => handleUpdateSelectedDrill(item.instanceId, 'targetLocation', e.target.value)} />
                        <div className="qty">
                          <button type="button" onClick={() => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', Math.max(0, item.numberOfBalls - 1))}>-</button>
                          <input type="text" inputMode="numeric" value={item.numberOfBalls} aria-label="balls" onChange={e => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', parseInt(e.target.value, 10) || 0)} />
                          <button type="button" onClick={() => handleUpdateSelectedDrill(item.instanceId, 'numberOfBalls', item.numberOfBalls + 1)}>+</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {showDrillSelectionPopup && (
        <DrillSelectionPopup
          deviceId={deviceId}
          selectedDrills={selectedDrills}
          setSelectedDrills={setSelectedDrills}
          onClose={() => { setShowDrillSelectionPopup(false); setShowFooter(true); }}
          onDeleteDrill={handleCustomDrillDeleteRequest}          dataV1={dataV1}
          dataSemiRandom={dataSemiRandom}
          dataCustomDrills={dataCustomDrills}
          drills={drills}
        />
      )}

      {showFooter && (
        <div className="footer">
          <div className={`bar ${onlyClearButtonVisible ? 'center-single-button' : ''}`}>
            <button id="clearBtn" className="btn ghost" onClick={handleClearSelected}><Plus /> {(!isUpdateMode && newSessionName.trim() === '' && selectedDrills.length === 0) ? 'Create New Session' : 'Clear'}</button>
            {!isUpdateMode && newSessionName.trim() !== '' && selectedDrills.length > 0 && (
              <button id="saveBtn" className="btn brand" onClick={() => handleSaveOrUpdateSession()}><Save /> Save Session</button>
            )}
            {isUpdateMode && hasChanges && (
              <button id="saveBtn" className="btn brand" onClick={() => handleSaveOrUpdateSession()}><Save /> Update Session</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateSession;